import { useCallback, useEffect, useMemo, useState } from "react";
import { FiEdit2, FiPlus, FiSearch, FiTrash2, FiXCircle } from "react-icons/fi";

import AppBreadcrumbs from "components/common/AppBreadcrumbs";
import AppDataTable from "components/common/AppDataTable";
import AppPagination from "components/common/AppPagination";
import ListFetchErrorBanner from "components/common/ListFetchErrorBanner";
import StatusBadge from "components/common/StatusBadge";
import Button from "components/Button";
import Card from "components/Card";
import FormTextInput from "components/form/FormTextInput";
import { ApiError, api, isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import { confirmAction } from "lib/alerts/appAlerts";
import swtAlert from "lib/sweetAlert";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { buildQueryString, formatTs, nonEmptyStr } from "utils/helper";
import { normalizeIndustriesListResponse } from "utils/industriesListResponse";
import { noWrap } from "utils/formatters";

const DEFAULT_LIMIT = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const FILTER_INITIAL = { page: 1, limit: DEFAULT_LIMIT, search: "" };

function mapIndustryRow(raw) {
  const id = raw.id ?? raw._id ?? "";
  return {
    id: String(id),
    name: nonEmptyStr(raw.name, "—"),
    isActive: Boolean(raw.isActive ?? raw.is_active ?? true),
    updatedAt: formatTs(raw.updatedAt ?? raw.updated_at ?? raw.createdAt ?? raw.created_at),
  };
}

function ActiveCell(row) {
  return <StatusBadge status={row.isActive ? "Active" : "Inactive"} />;
}

export default function IndustriesPage() {
  const [filters, setFilters] = useState(FILTER_INITIAL);
  const [payload, setPayload] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [searchDraft, setSearchDraft] = useState(filters.search);
  const { debounced: debouncedSearch, flush: flushSearch } = useDebouncedValue(searchDraft, 400);

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    setFilters((prev) => {
      const nextSearch = debouncedSearch.trim();
      if (prev.search === nextSearch) return prev;
      return { ...prev, search: nextSearch, page: 1 };
    });
  }, [debouncedSearch]);

  useEffect(() => {
    setListError("");
  }, [filters]);

  const [panelMode, setPanelMode] = useState("none");
  const [editingId, setEditingId] = useState("");
  const [draft, setDraft] = useState({ name: "", isActive: true });
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const rows = useMemo(() => (payload.data ?? []).map(mapIndustryRow), [payload.data]);
  const totalItems = payload.total ?? 0;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalItems / (payload.limit || filters.limit || DEFAULT_LIMIT))),
    [totalItems, payload.limit, filters.limit],
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const qs = buildQueryString(filters);
      const path = qs ? `${API_ENDPOINTS.industries}?${qs}` : API_ENDPOINTS.industries;
      const resp = await api.get(path);
      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Could not load industries.";
        setListError(msg);
        swtAlert.error("Industries", msg);
        return;
      }
      const { list, total, limit, page } = normalizeIndustriesListResponse(
        resp,
        filters.limit,
        filters.page,
      );
      setPayload({
        data: list,
        total,
        limit,
        page,
      });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err?.message || "Could not load industries.";
      setListError(msg);
      swtAlert.error("Industries", msg);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openCreate = () => {
    setPanelMode("create");
    setEditingId("");
    setDraft({ name: "", isActive: true });
    setFormError("");
  };

  const openEdit = useCallback((row) => {
    setPanelMode("edit");
    setEditingId(row.id);
    setDraft({ name: row.name === "—" ? "" : row.name, isActive: row.isActive });
    setFormError("");
  }, []);

  const closePanel = () => {
    setPanelMode("none");
    setEditingId("");
    setDraft({ name: "", isActive: true });
    setFormError("");
  };

  const saveIndustry = async () => {
    const name = String(draft.name ?? "").trim();
    if (!name) {
      setFormError("Name is required.");
      return;
    }
    setFormError("");
    setSaving(true);
    try {
      const body = { name, isActive: Boolean(draft.isActive) };
      if (panelMode === "create") {
        const resp = await api.post(API_ENDPOINTS.industries, body);
        if (!isSuccessResponse(resp)) {
          swtAlert.error("Industry", resp?.message || "Could not create industry.");
          return;
        }
        swtAlert.success("Industry created", "The industry was added.");
      } else if (panelMode === "edit" && editingId) {
        const resp = await api.patch(API_ENDPOINTS.industryById(editingId), body);
        if (!isSuccessResponse(resp)) {
          swtAlert.error("Industry", resp?.message || "Could not update industry.");
          return;
        }
        swtAlert.success("Industry updated", "Changes were saved.");
      }
      closePanel();
      await fetchList();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err?.message || "Request failed.";
      swtAlert.error("Industry", msg);
    } finally {
      setSaving(false);
    }
  };

  const deleteIndustry = useCallback(async (row) => {
    const ok = await confirmAction({
      title: "Delete industry?",
      text: `Remove “${row.name}”? This cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
    });
    if (!ok) return;
    try {
      await api.delete(API_ENDPOINTS.industryById(row.id));
      swtAlert.success("Industry deleted", "The industry was removed.");
      await fetchList();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err?.message || "Could not delete.";
      swtAlert.error("Delete failed", msg);
    }
  }, [fetchList]);

  const columns = useMemo(
    () => [
      { id: "sr_no", header: noWrap("#"), cell: (_r, i) => i + 1, width: "56px" },
      { id: "name", header: noWrap("Name"), accessor: "name", sortable: true },
      { id: "isActive", header: noWrap("Status"), cell: ActiveCell, sortable: true },
      { id: "updatedAt", header: noWrap("Updated"), accessor: "updatedAt", sortable: true },
      {
        id: "actions",
        header: noWrap("Actions"),
        cell: (row) => (
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              title="Edit"
              onClick={() => openEdit(row)}
              className="rounded-md border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
            >
              <FiEdit2 size={14} />
            </button>
            <button
              type="button"
              title="Delete"
              onClick={() => deleteIndustry(row)}
              className="rounded-md border border-slate-200 p-2 text-danger transition hover:bg-red-50"
            >
              <FiTrash2 size={14} />
            </button>
          </div>
        ),
        width: "120px",
      },
    ],
    [openEdit, deleteIndustry],
  );

  const formPanel =
    panelMode !== "none" ? (
      <Card className="mb-4 border-slate-200" title={panelMode === "create" ? "New industry" : "Edit industry"}>
        <div className="grid gap-4 md:grid-cols-2">
          <FormTextInput
            label="Name"
            name="industryName"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder="e.g. Technology"
            error={formError}
          />
          <div className="flex items-end pb-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-text-800">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => setDraft((d) => ({ ...d, isActive: e.target.checked }))}
                className="h-4 w-4 rounded border-slate-300 text-brand-900 focus:ring-brand-600"
              />
              Active
            </label>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button type="button" onClick={saveIndustry} disabled={saving}>
            {saving ? "Saving…" : panelMode === "create" ? "Create" : "Save changes"}
          </Button>
          <Button type="button" variant="secondary" onClick={closePanel} disabled={saving}>
            Cancel
          </Button>
        </div>
      </Card>
    ) : null;

  const headerAction = (
    <Button type="button" onClick={openCreate} className="inline-flex items-center gap-2">
      <FiPlus className="h-4 w-4" aria-hidden />
      Add industry
    </Button>
  );

  return (
    <>
      <AppBreadcrumbs items={[{ label: "Industries", path: "/industries" }]} />

      <Card title="Industries" action={headerAction} className="mb-4 border-slate-200" />

      {formPanel}

      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-text-900">All industries</h3>
            <p className="text-xs text-text-500">Total: {totalItems}</p>
          </div>

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <div className="relative min-w-[200px] flex-1 sm:max-w-sm">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-500" />
              <input
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    flushSearch();
                    setFilters((prev) => ({
                      ...prev,
                      search: searchDraft.trim(),
                      page: 1,
                    }));
                  }
                }}
                placeholder="Search by name…"
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none ring-brand-600 focus:ring-2"
              />
            </div>
            {(searchDraft || filters.search) && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => {
                  setSearchDraft("");
                  setFilters((prev) => ({ ...prev, search: "", page: 1 }));
                }}
                className="rounded-lg border border-slate-200 p-2 text-text-700 transition hover:bg-slate-50"
              >
                <FiXCircle />
              </button>
            )}
          </div>
        </div>

        {listError ? (
          <ListFetchErrorBanner message={listError} onRetry={fetchList} loading={loading} />
        ) : null}

        <AppDataTable columns={columns} data={rows} rowKey="id" loading={loading} />
        <AppPagination
          currentPage={filters.page}
          totalPages={totalPages}
          onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
          totalItems={totalItems}
          pageSize={filters.limit}
          onPageSizeChange={(limit) => setFilters((f) => ({ ...f, limit, page: 1 }))}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </Card>
    </>
  );
}
